/**
 * React provider + hooks for the per-user Yjs notes document.
 *
 * The underlying WebSocket + Y.Doc is owned by `<UserDoConnectionProvider>`
 * (see `src/sync/user-do-connection.tsx`). That socket is shared with
 * UserDo LWW sync — one connection per signed-in user.
 *
 * `<UserNotesDocProvider>` wraps that Y.Doc in a `UserNotesDoc` facade,
 * manages IndexedDB persistence, and registers the active doc for non-
 * React callers. It renders a `{ userDoc: null, synced: false }` context
 * when the connection isn't available (signed out / not hydrated).
 *
 * That one doc hosts ALL the user's collaborative state across every
 * deck — notes today, annotations/handwriting/embeds later.
 *
 * `useDeckDoc(deckName)` is a CHEAP lookup: it returns a DeckDoc view
 * over the deck's sub-map inside the user doc. Zero network, zero IDB,
 * zero hook-mount cost beyond a useMemo.
 */

/// <reference path="./y-tiptap-shim.d.ts" />
import {
  prosemirrorJSONToYXmlFragment,
  yXmlFragmentToProsemirrorJSON,
} from '@tiptap/y-tiptap';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type YProvider from 'y-partyserver/provider';
import * as Y from 'yjs';

import {
  type NoteFragmentConverters,
  seedNoteFragmentIfEmpty,
} from '../../notes/seed-note-fragment';
import type { Editor, JSONContent } from '../internal/editor-types';
import { pushSyncMessage } from '../internal/message-log';
import { createYjsPersistence, type YjsPersistence } from '../internal/yjs-persistence';
import { useUserDoConnection } from '../provider/user-do-connection';
import {
  getActiveUserNotesDoc,
  registerActivePersonalScope,
  registerActiveUserNotesDoc,
} from './active-deck-docs';
import { DEFAULT_NOTE_ID, UserNotesDoc } from './deck-doc';
import { useNoteDocSpawnConfig } from './note-doc-spawn-context';
import {
  UserNotesDocContext,
  type UserNotesDocContextValue,
} from './user-notes-doc-context';

/**
 * IndexedDB store name for the per-user ROOT Y.Doc.
 *
 * Versioned `:v2` because the pre-refactor Y.Doc contained all decks +
 * notes nested as Y.Maps; that data shape is gone and trying to merge
 * it into the new (metadata-only) root would only produce noise. The
 * old `flashka-yjs:{userId}` store is orphaned. Per-note Y.Docs use a
 * separate naming scheme (`flashka-note:...`) — see `note-doc.ts`.
 */
function idbName(userId: string): string {
  return `flashka-yjs-root:v2:${userId}`;
}

/**
 * The sync-client's Yjs↔ProseMirror pair, handed to the shared seed.
 *
 * Resolved from `@tiptap/y-tiptap` — which flashka-app's Metro redirects to a
 * throwing stub, so mobile supplies its own `y-prosemirror` pair instead of
 * importing this. That split is precisely why the shared seed takes the
 * converters as a parameter.
 */
const Y_TIPTAP_CONVERTERS: NoteFragmentConverters = {
  toJSON: (fragment) => yXmlFragmentToProsemirrorJSON(fragment),
  fromJSON: (schema, content, fragment) =>
    prosemirrorJSONToYXmlFragment(
      schema as Parameters<typeof prosemirrorJSONToYXmlFragment>[0],
      content as Parameters<typeof prosemirrorJSONToYXmlFragment>[1],
      fragment as Parameters<typeof prosemirrorJSONToYXmlFragment>[2],
    ),
};

/** The personal-scope party — must match `useUserDoConnection.tsx`. */
const USER_DO_PARTY = 'user-do';

// ---------------------------------------------------------------------------
// UserNotesDocProvider — mount ONCE at app level (inside SyncProvider)
// ---------------------------------------------------------------------------

/**
 * Props for `UserNotesDocProvider`. `userId` is allowed to be null/undefined
 * (meaning "not signed in / not ready yet"); the component renders the
 * context with `{ userDoc: null, synced: false }` in that case.
 */
interface UserNotesDocProviderProps {
  userId: string | null | undefined;
  children: React.ReactNode;
}

/**
 * Mount the per-user Yjs document and provide it to descendants via
 * `UserNotesDocContext`.
 *
 * Split into two components: the outer one branches on `userId` +
 * connection availability, and only the inner
 * `ConnectedUserNotesDocProvider` actually reads the Y.Doc and opens the
 * IndexedDB handle. The underlying WebSocket is owned by
 * `<UserDoConnectionProvider>` — we reuse it here rather than opening a
 * second socket.
 */
export function UserNotesDocProvider({
  userId,
  children,
}: UserNotesDocProviderProps): React.ReactElement {
  const provider = useUserDoConnection();
  if (!userId || !provider) {
    return (
      <UserNotesDocContext.Provider
        value={{
          userDoc: null,
          synced: false,
          idbSynced: false,
          wsSynced: false,
        }}>
        {children}
      </UserNotesDocContext.Provider>
    );
  }
  return (
    <ConnectedUserNotesDocProvider userId={userId} provider={provider}>
      {children}
    </ConnectedUserNotesDocProvider>
  );
}

/**
 * Connected variant — only mounted once a live provider is present. Reads
 * the Y.Doc off the shared UserDo connection, wraps it in a `UserNotesDoc`,
 * opens an IndexedDB handle, and tracks the `synced` flag.
 *
 * Remount on userId change (e.g. sign-out → sign-in as a different user)
 * is handled by the outer React `key={userId}` that the parent applies —
 * see `SyncProvider` for how that's wired.
 */
function ConnectedUserNotesDocProvider({
  userId,
  provider,
  children,
}: {
  userId: string;
  provider: YProvider;
  children: React.ReactNode;
}): React.ReactElement {
  const ydoc = provider.doc;

  const userDoc = useMemo<UserNotesDoc>(() => new UserNotesDoc(ydoc), [ydoc]);

  // ── IndexedDB offline persistence ──────────────────────────────────
  // Exactly one IDB handle per session. Any edit persists locally AND
  // sends upstream; any state read from IDB on startup is applied to the
  // Y.Doc and then reconciled with the server on connect.
  //
  // CRITICAL: `synced` (the consumer-facing flag) requires BOTH IDB and
  // WebSocket sync to have completed (with a timeout fallback for offline
  // mode). Auto-creating Y.Map structure (e.g. `notes.fragment()`) before
  // the WebSocket has reconciled risks a CRDT race: local empty maps can
  // win the tiebreaker against the server's rich data, wiping the user's
  // notes. See `notes-view.tsx` for the call-site gate.
  const [idbSynced, setIdbSynced] = useState(false);
  const [wsSynced, setWsSynced] = useState<boolean>(() => provider.synced);
  const [offlineFallback, setOfflineFallback] = useState(false);
  // Track the IDB persistence instance so the Yjs-update observer (below)
  // can classify updates originating from an IDB replay separately from
  // remote-server updates.
  const idbRef = useRef<YjsPersistence | null>(null);
  useEffect(() => {
    const idb = createYjsPersistence(idbName(userId), ydoc);
    idbRef.current = idb;
    const onLoaded = () => setIdbSynced(true);
    idb.on('synced', onLoaded);
    return () => {
      idb.off('synced', onLoaded);
      void idb.destroy();
      if (idbRef.current === idb) idbRef.current = null;
    };
  }, [ydoc, userId]);

  // Offline fallback: if IDB has hydrated but WebSocket hasn't synced
  // within OFFLINE_FALLBACK_MS, allow `synced` to flip true on IDB alone
  // so the user can still create notes on a brand-new deck while offline.
  // The race (IDB-empty + WS-late) is the dangerous case; this fallback
  // is intentionally generous so we'd rather show a brief loading state
  // than wipe data.
  useEffect(() => {
    if (!idbSynced || wsSynced) return;
    const id = setTimeout(() => setOfflineFallback(true), 8000);
    return () => clearTimeout(id);
  }, [idbSynced, wsSynced]);

  const synced = wsSynced || (idbSynced && offlineFallback);

  // ── Message log (shared with UserDO) ───────────────────────────────
  // Every Yjs transaction produces an `update` event with a binary payload.
  // We classify by `origin`:
  //   - null/undefined → local edit (will be flushed out by the provider)
  //   - === provider   → remote update applied by the provider
  //   - === idbRef     → replay from local IndexedDB on startup
  // Size = update.byteLength is a rough proxy for on-the-wire traffic for
  // local/remote; for IDB replays it's pure local transfer.
  useEffect(() => {
    const handler = (update: Uint8Array, origin: unknown) => {
      let direction: 'out' | 'in' | 'idb';
      let detail: string;
      if (origin === idbRef.current) {
        direction = 'idb';
        detail = 'IndexedDB replay';
      } else if (origin != null && origin === provider) {
        direction = 'in';
        detail = 'remote → local';
      } else if (origin == null) {
        direction = 'out';
        detail = 'local edit';
      } else {
        // Unknown origin (e.g. setContent via Tiptap). Treat as local so
        // it shows up as outgoing traffic.
        direction = 'out';
        const name =
          typeof origin === 'object'
            ? ((origin as { constructor?: { name?: string } }).constructor?.name ??
              'Object')
            : String(origin);
        detail = `local (${name})`;
      }
      pushSyncMessage({
        // NotesDO was consolidated into UserDO — the Y.Doc lives on
        // the UserDO socket now, so every update logged here is a
        // UserDO message.
        source: 'user-do',
        direction,
        type: 'yjs-update',
        ts: Date.now(),
        sizeBytes: update.byteLength,
        detail: detail.startsWith('IndexedDB') ? detail : `notes ${detail}`,
      });
    };
    ydoc.on('update', handler);
    return () => {
      ydoc.off('update', handler);
    };
  }, [ydoc, provider]);

  // ── `synced` tracking (WebSocket path) ─────────────────────────────
  useEffect(() => {
    const onSync = (isSynced: boolean) => {
      if (isSynced) setWsSynced(true);
    };
    provider.on('sync', onSync);
    if (provider.synced) setWsSynced(true);
    return () => {
      provider.off('sync', onSync);
    };
  }, [provider]);

  // ── Register in the active-doc registry for non-React callers ──────
  useEffect(() => {
    const unregister = registerActiveUserNotesDoc(userDoc);
    return unregister;
  }, [userDoc]);

  // ── Register the active personal-scope spawn config ────────────────
  // Non-React `ScopedNotes` accessors use this to peek the per-note
  // registry (`note-doc-registry.ts`) for the right (party, room).
  // `host` + `getToken` come along so non-React callers can also
  // *acquire* transient per-note Y.Docs — e.g. `sharing.share()` migrating
  // the personal NoteDoc's content into the SG NoteDoc at share time.
  const spawnCfg = useNoteDocSpawnConfig();
  const spawnHost = spawnCfg?.host;
  const spawnGetToken = spawnCfg?.getToken;
  useEffect(() => {
    const unregister = registerActivePersonalScope({
      party: USER_DO_PARTY,
      room: userId,
      host: spawnHost,
      getToken: spawnGetToken,
    });
    return unregister;
  }, [userId, spawnHost, spawnGetToken]);

  return (
    <UserNotesDocContext.Provider value={{ userDoc, synced, idbSynced, wsSynced }}>
      {children}
    </UserNotesDocContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Legacy JSON → Y.XmlFragment seed (per-deck, called from notes-view)
// ---------------------------------------------------------------------------

/**
 * One-time bootstrap: if the deck has no notes yet in the user doc AND
 * there's stashed legacy JSON from the Supabase migration, seed the
 * deck's default tiptap note from that JSON.
 *
 * The seed uses the live editor's ProseMirror `Schema` to parse JSON —
 * without the schema we'd silently drop node types.
 *
 * Returns the outcome so the caller knows whether to consume the stash:
 * `'seeded'` (we wrote it) and `'already-present'` (real content was already
 * there) are both safe to consume; `'skipped'` (no live fragment / convert
 * failed) means KEEP the stash and retry on a later mount.
 *
 * Race guard: the check-and-seed runs inside a single Y transaction.
 * Concurrent tabs could both see an empty doc and double-seed (CRDT merges
 * it) — an accepted corner case, same as before.
 *
 * Safe to call whenever the notes facade + editor are available — it only
 * (re)seeds an empty / single-empty-paragraph doc; real content is left.
 */
export type LegacySeedResult = 'seeded' | 'already-present' | 'skipped';

export function seedNotesFromLegacyIfEmpty(
  notes: import('../scoped-deck').ScopedNotes,
  editor: Editor,
  legacyContent: JSONContent | undefined | null,
  noteId: string = DEFAULT_NOTE_ID,
): LegacySeedResult {
  if (!legacyContent) return 'skipped';

  let result: LegacySeedResult = 'skipped';
  notes.transact(() => {
    // Force-create the tiptap fragment (auto-creates empty on first call).
    const fragment = notes.fragment(noteId);
    if (!fragment) {
      // No live tiptap fragment (doc not ready / non-tiptap note) — don't
      // seed; KEEP the stash so a later mount can retry.
      result = 'skipped';
      return;
    }

    // The emptiness gate, the delete-then-append ordering and the result
    // contract live in `seedNoteFragmentIfEmpty`, shared with mobile's notes
    // editor so both platforms seed under identical rules. `skipped` still
    // means "KEEP the stash for a later retry".
    result = seedNoteFragmentIfEmpty({
      fragment,
      // `editor.schema` is `unknown` on our structural Editor (to avoid pinning
      // to a Tiptap major); the converter casts at its own boundary.
      schema: editor.schema,
      content: legacyContent,
      converters: Y_TIPTAP_CONVERTERS,
    });
  });
  return result;
}

// ---------------------------------------------------------------------------
// Read helpers (for non-editor call-sites)
// ---------------------------------------------------------------------------

/**
 * Convert a Y.XmlFragment to ProseMirror JSON. Used by call-sites that
 * need to serialise notes outside of a live editor (share-deck, AI fill,
 * mnemonics, transcript seeding).
 */
export function noteFragmentToJSON(fragment: Y.XmlFragment): JSONContent | undefined {
  try {
    return yXmlFragmentToProsemirrorJSON(fragment) as JSONContent;
  } catch (err) {
    console.error('[Yjs read] failed to serialise fragment', err);
    return undefined;
  }
}

// Re-export the user-doc registry for internal callers (scoped-deck,
// deck-slice). `getActiveDeckDoc` and `readActiveDeckNotesJSON` are gone
// — use `sync.decks.deck(id).notes.*` instead.
export { getActiveUserNotesDoc };
export type { UserNotesDocContextValue };
